<template>
	<div class="participants-wrapper">
		<div
			v-if="participants.length > 0"
			ref="participantsSection"
			v-dragscroll="true"
			class="participant-list"
		>
			<div
				v-for="participant in participants"
				:key="participant.id"
				class="participant-list-item"
			>
				<div class="participant-inner">
					<img
						:src="participant.image"
						:alt="participant.name"
						class="participant-list-item-image"
					>
					<a
						v-if="participant.url"
						:href="participant.url"
						target="_blank"
					>{{ useShortName ? participant.code : participant.name }}</a>
					<span
						v-else
						class="participant-list-item-name"
					>{{ useShortName ? participant.code : participant.name }}</span>
				</div>
			</div>
		</div>
		<div
			v-if="!isMobileWebApp && showLeftArrow"
			class="arrow-left"
			:class="{active: activeScroll === 'left'}"
			@mousedown="startScrollLeft"
			@mouseup="cancelScrolling"
			@touchstart="startScrollLeft"
			@touchend="cancelScrolling"
		>
			<Icon
				icon-name="left-arrow"
				size="sm"
			/>
		</div>
		<div v-else />
		<div
			v-show="!isMobileWebApp && showRightArrow"
			class="arrow-right"
			:class="{active: activeScroll === 'right'}"
			@mousedown="startScrollRight"
			@mouseup="cancelScrolling"
			@touchstart="startScrollRight"
			@touchend="cancelScrolling"
		>
			<Icon
				icon-name="right-arrow"
				size="sm"
			/>
		</div>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';

const SCROLL_STEP = 20;

export default {
	name:  'ParticipantsHorizontalList',
	props: {
		participants: {
			type:     Array,
			required: true,
		},
	},
	data() {
		return {
			showRightArrow: false,
			showLeftArrow:  false,
			interval:       null,
			scrollOffset:   0,
			activeScroll:   null,
		};
	},
	computed: {
		...mapGetters({
			isTeamCompetition: 'event/isTeamCompetition',
			isMobileWebApp:    'app/isMobileWebApp',
		}),
		useShortName() {
			return this.isMobileWebApp && this.isTeamCompetition;
		},
	},
	watch: {
		participants() {
		  this.showHideArrows();
		},
	},
	mounted() {
		this.$nextTick(this.showHideArrows);
	},
	methods: {
		showHideArrows() {
			const el = this.$refs.participantsSection;
			if (!el) {
			  return;
			}
			this.showRightArrow = el.scrollWidth > el.clientWidth + this.scrollOffset;
			this.showLeftArrow = this.scrollOffset > 0;
		},
		scrollRight() {
			const el = this.$refs.participantsSection;
			this.scrollOffset = this.scrollOffset + SCROLL_STEP;

			if (el.scrollWidth - this.scrollOffset < el.clientWidth) {
				this.scrollOffset = el.scrollWidth - el.clientWidth;
				this.cancelScrolling();
			}
			this.scrollElement(el);
			this.showHideArrows();
		},
		scrollLeft() {
			const el = this.$refs.participantsSection;
			this.scrollOffset = Math.max(0, this.scrollOffset - SCROLL_STEP);

			if (this.scrollOffset === 0) {
				this.cancelScrolling();
			}
			this.scrollElement(el);
			this.showHideArrows();
		},
		startScrollLeft() {
			clearInterval(this.interval);
			this.activeScroll = 'left';
			this.interval = setInterval(this.scrollLeft, 20);
		},
		startScrollRight() {
			clearInterval(this.interval);
			this.activeScroll = 'right';
			this.interval = setInterval(this.scrollRight, 20);
		},
		cancelScrolling() {
			clearInterval(this.interval);
			this.activeScroll = null;
		},
		scrollElement(el) {
			el.scrollLeft = this.scrollOffset;
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';
@import '@videotools/sass/_fonts';
@import '@shared/sass/shared-variables';

.participants-wrapper {
	position: relative;
	width: 100%;

	.arrow-left, .arrow-right {
		visibility: hidden;
		position: absolute;
		top: 16px;
		height: 32px;
		width: 32px;
		border-radius: 16px;
		display: flex;
		justify-content: center;
		align-items: center;
		padding: 4px;
		background: var(--color-grey-900);
		color: var(--color-white);
		transition: all 250ms;

		@media (hover: hover) {
			&:hover {
				background: var(--color-hype-yellow);
				color: var(--color-black);
				cursor: pointer;
			}
		}

		&.active {
			background: var(--color-hype-yellow);
			color: var(--color-black);
		}
	}

	.arrow-left {
		justify-self: start;
		left: 16px;
	}

	.arrow-right {
		justify-self: end;
		right: 16px;
	}

  @media (hover: hover) {
		&:hover {
			.arrow-left, .arrow-right {
				visibility: visible;
			}
		}
  }
}

.participant-list {
	display: flex;
	overflow: hidden;
	transition: transform 150ms;
	padding: 8px 0;

	.is-mobile & {
		border-bottom: 1px solid var(--color-grey-900);
	}

	.participant-list-item {
		padding: 8px 24px;
		font-size: 16px;

		.participant-inner {
			display: flex;
			align-items: center;
			padding: 4px 0;

			.is-mobile & {
				padding: 0;
			}
		}

		& + .participant-list-item {
			border-left: 1px solid var(--color-grey-900);
		}

		.participant-list-item-image {
			max-width: initial;
			width: 24px;
			height: 24px;
			margin-right: 12px;

			border-radius: 100%;
			object-fit: cover;
			vertical-align: middle;
		}

		.participant-list-item-name {
			white-space: nowrap;
		}

		a:hover {
			color: var(--color-hype-yellow);
		}
	}
}

</style>
